import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useContext,
} from 'react';

import { Stack } from '@mui/system';
import { Box, useMediaQuery, useTheme, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PostData, FollowData } from '../model/types';
import Post from './postComponent';
import { CacheContext, CacheContextType } from '../context/CacheContext';

/**
 * REPLIES — nested reply tree with a visible-depth cap.
 *
 * Previously this was an unbounded recursion: `Post → PostActions → Replies
 * → Post` with no depth counter, and each level added a 20px thread
 * connector column. On a 375px iPhone SE a depth-8 thread consumed ~160px
 * (43%) of horizontal budget before any content rendered — effectively
 * unusable. Reddit, Bluesky, and Twitter all solve this by capping visual
 * nesting and pivoting deeper threads to a dedicated sub-thread page.
 *
 * Design (researched 2026-04-07):
 *  - Mobile (< 600px): cap at REPLY_DEPTH_CAP_MOBILE = 3, connector = 14px
 *  - Desktop: cap at REPLY_DEPTH_CAP_DESKTOP = 5, connector = 18px
 *  - At the cap, render a single "[ CONTINUE · N REPLIES → ]" pixel button
 *    linking to /posts/:parentId. PostPageComponent shows the focused post
 *    as a new root (depth=0), so pivoting gets the full budget again.
 *  - Only the first REPLY_SIBLING_LIMIT siblings render inline at each
 *    level. Extras collapse behind a "[ SHOW N MORE REPLIES → ]" link that
 *    navigates to the post page for a full view.
 *
 * The vertical connector line on the left is the single strongest visual
 * signal that "this is a reply to the thing above" — Tildes tried dropping
 * it in favor of text labels and users couldn't tell siblings from
 * parent-child at the same indent. We keep it.
 */

const REPLY_DEPTH_CAP_MOBILE = 3;
const REPLY_DEPTH_CAP_DESKTOP = 5;
const REPLY_SIBLING_LIMIT = 5;

function Replies({
  parentObject,
  expandToReply,
  setShowReplies,
  depth = 0,
}: {
  parentObject: PostData | FollowData;
  expandToReply?: string;
  setShowReplies: any;
  /**
   * Current depth in the reply tree. 0 at a root-level post. Used to
   * decide whether to keep recursing or swap in a "Continue thread" link.
   */
  depth?: number;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cap = isMobile ? REPLY_DEPTH_CAP_MOBILE : REPLY_DEPTH_CAP_DESKTOP;
  const connectorWidth = isMobile ? 14 : 18;

  let getPosts = useContext<CacheContextType>(CacheContext).allPosts;
  const [posts, setPosts] = useState<PostData[]>([]);
  const replyRef = useRef(null);
  const executeScroll = () => {
    // replyRef is not set currently
    // replyRef?.current?.scrollIntoView();
  };

  useLayoutEffect(() => {
    setTimeout(() => executeScroll(), 500);
  }, []);

  useEffect(() => {
    let snapshotPosts = getPosts();
    setPosts(snapshotPosts.filter((post) => post.parent_id === parentObject.id));

    if (expandToReply !== undefined) {
      setShowReplies(true);
      let foundReply =
        parentObject.id === expandToReply;
      if (foundReply) {
        executeScroll();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPosts]);

  if (!parentObject || !posts || posts === undefined || posts.length === 0)
    return (
      <Box sx={{ color: 'text.secondary', fontSize: '0.8rem', py: 1.5, px: 1, fontFamily: '"VT323", monospace', letterSpacing: 1, opacity: 0.6 }}>
        [ NO REPLIES YET ]
      </Box>
    );

  const sorted = posts.sort((a, b) =>
    a.created_at > b.created_at ? -1 : a.created_at < b.created_at ? 1 : 0,
  );

  // At the depth cap we stop rendering nested children in place. Replace
  // the subtree with a single pivot link to the parent's post page.
  if (depth >= cap) {
    return (
      <Box sx={{ pt: 0.5, pb: 1, pl: `${connectorWidth}px` }}>
        <Button
          component={Link}
          to={`/posts/${parentObject.id}`}
          size="small"
          variant="outlined"
          endIcon={<ChevronRightIcon />}
          sx={{
            fontFamily: '"VT323", monospace',
            letterSpacing: 1.5,
            fontSize: '0.8rem',
            borderRadius: 0,
            py: 0.5,
            textTransform: 'uppercase',
          }}
        >
          [ Continue · {posts.length} {posts.length === 1 ? 'reply' : 'replies'} ]
        </Button>
      </Box>
    );
  }

  // Under the cap we render inline but still limit the number of siblings
  // at each level so a single popular post can't paint 200 children.
  const visible = sorted.slice(0, REPLY_SIBLING_LIMIT);
  const overflow = sorted.length - visible.length;

  return (
    <Stack spacing={0} sx={{ mt: 0.5 }}>
      {visible.map((reply, idx) => {
        const isLast = idx === visible.length - 1 && overflow === 0;
        return (
          <Box
            key={reply.id}
            id={reply.id}
            ref={reply.id === expandToReply ? replyRef : null}
            sx={{ display: 'flex', gap: 0 }}
          >
            {/* Thread connector column — narrower on mobile to preserve
                content width. The horizontal stub visually joins the reply
                to the vertical spine. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: connectorWidth,
                flexShrink: 0,
                pt: 1.5,
              }}
            >
              <Box sx={{ width: '100%', height: 2, bgcolor: 'divider', mt: 0.75, mb: 0 }} />
              {!isLast && (
                <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 1, pt: 0.5 }}>
              <Post post={reply} expandToReply={expandToReply} depth={depth + 1} />
            </Box>
          </Box>
        );
      })}

      {overflow > 0 && (
        <Box sx={{ pl: `${connectorWidth}px`, pt: 0.5, pb: 0.5 }}>
          <Button
            component={Link}
            to={`/posts/${parentObject.id}`}
            size="small"
            variant="text"
            endIcon={<ChevronRightIcon />}
            sx={{
              fontFamily: '"VT323", monospace',
              letterSpacing: 1.5,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
            }}
          >
            [ Show {overflow} more {overflow === 1 ? 'reply' : 'replies'} ]
          </Button>
        </Box>
      )}
    </Stack>
  );
}
export default Replies;
